import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, Phone } from "lucide-react";
import { useCart } from "../context/CartContext";
import { CONTACT } from "../lib/constants";

const NAV = [
  { label: "Accueil", to: "/" },
  { label: "Boutique", to: "/boutique" },
  { label: "Nouveautés", to: "/boutique?category=nouveautes" },
  { label: "À propos", to: "/a-propos" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const { totalItems } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/boutique?search=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0A0A0A] text-[#F4E5A1] text-xs md:text-sm py-2 px-4 text-center tracking-wide">
        Livraison rapide à {CONTACT.city} • Paiement à la livraison • Commande en 2 clics via WhatsApp
      </div>

      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_6px_24px_-16px_rgba(0,0,0,0.4)]" : "bg-white"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src="/maxichoix-logo.jpeg" alt="MaxiChoix" className="h-12 w-12 object-contain rounded-full" />
            <span className="font-display text-2xl md:text-3xl font-semibold tracking-wide">
              <span className="text-[#0A0A0A]">MAXI</span>
              <span className="gold-text">CHOIX</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="link-underline text-sm uppercase tracking-widest text-[#1a1a1a] hover:text-[#B8860B] transition-colors"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s|à/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3 md:gap-5">
            <form onSubmit={submitSearch} className="hidden md:flex items-center relative">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher…"
                className="w-40 lg:w-52 border border-neutral-200 rounded-full py-2 pl-4 pr-10 text-sm focus:outline-none focus:border-[#B8860B] transition"
                data-testid="search-input"
              />
              <button type="submit" className="absolute right-3 text-neutral-500" data-testid="search-submit">
                <Search size={16} />
              </button>
            </form>

            <Link to="/panier" className="relative" data-testid="cart-button">
              <ShoppingBag size={24} className="text-[#0A0A0A]" />
              {totalItems > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-[#B8860B] text-white text-[10px] font-semibold w-5 h-5 rounded-full flex items-center justify-center"
                  data-testid="cart-count"
                >
                  {totalItems}
                </span>
              )}
            </Link>

            <button
              className="lg:hidden text-[#0A0A0A]"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white p-6 flex flex-col animate-in slide-in-from-right">
            <div className="flex items-center justify-between mb-8">
              <span className="font-display text-2xl font-semibold">
                <span className="text-[#0A0A0A]">MAXI</span>
                <span className="gold-text">CHOIX</span>
              </span>
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close">
                <X size={26} />
              </button>
            </div>
            <form onSubmit={submitSearch} className="flex items-center relative mb-6">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un produit…"
                className="w-full border border-neutral-200 rounded-full py-3 pl-4 pr-10 text-sm focus:outline-none focus:border-[#B8860B]"
              />
              <button type="submit" className="absolute right-4 text-neutral-500">
                <Search size={18} />
              </button>
            </form>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="py-3 border-b border-neutral-100 text-base uppercase tracking-widest text-[#1a1a1a]"
                >
                  {n.label}
                </Link>
              ))}
            </nav>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              className="mt-auto flex items-center gap-2 text-[#B8860B] font-medium"
            >
              <Phone size={18} /> {CONTACT.phoneIntl}
            </a>
          </div>
        </div>
      )}
    </>
  );
}
